<template>
  <div class="category-management">
    <!-- 分类列表 -->
    <div class="category-list">
      <div class="category-item" v-for="category in categories" :key="category.id">
        <div class="category-info" @click="selectCategory(category)">
          <div class="category-name">{{ category.name }}</div>
          <div class="category-description">{{ category.description }}</div>
          <div class="image-count">{{ category.image_count || 0 }} 张图片</div>
        </div>
        <div class="category-actions">
          <button class="edit-btn" @click="editCategory(category)">编辑</button>
          <button class="delete-btn" @click="deleteCategory(category.id)">删除</button>
        </div>
      </div>
    </div>

        <!-- 添加分类按钮 -->
    <button @click="showAddForm" class="add-category-btn">+ 新增分类</button>

    <!-- 新增分类弹窗 -->
    <div v-if="showForm" class="modal-overlay" @click="cancelForm">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ isEditing ? '编辑分类' : '新增分类' }}</h3>
          <button class="close-btn" @click="cancelForm">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>分类名称:</label>
            <input v-model="form.name" type="text" placeholder="请输入分类名称" />
          </div>
          <div class="form-group">
            <label>分类描述:</label>
            <textarea v-model="form.description" placeholder="请输入分类描述"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="saveCategory" class="save-btn">{{ isEditing ? '更新' : '保存' }}</button>
          <button @click="cancelForm" class="cancel-btn">取消</button>
        </div>
      </div>
    </div>

    <!-- 图片管理区域 -->
    <div class="image-management" v-if="selectedCategory">
      <h3>{{ selectedCategory.name }} - 图片管理</h3>
      
      <!-- 上传图片 -->
      <div class="upload-section">
        <div class="upload-area" @click="triggerFileSelect">
          <div class="upload-content">
            <img src="../images/upload.png" alt="上传" class="upload-icon" />
            <p>点击上传图片</p>
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileUpload" 
              accept="image/*" 
              multiple 
              style="display: none;"
            />
          </div>
        </div>
      </div>

      <!-- 图片列表 -->
      <div class="image-grid">
        <div 
          class="image-item" 
          v-for="(image, index) in selectedCategory.images || []" 
          :key="index"
        >
          <img :src="processImageUrl(image)" :alt="selectedCategory.name" />
          <div class="image-actions">
            <button @click="removeImage(index)" class="remove-btn">删除</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { getCategoryList, createCategory, addImageToCategory, deleteCategory as apiDeleteCategory, deleteCategoryImage } from '@/api/category';
import { uploadImage } from '@/api/design';
import { useRouter } from 'vue-router';

// 响应式数据
const categories = ref([]);
const showForm = ref(false);
const isEditing = ref(false);
const selectedCategory = ref(null);
const form = ref({
  id: null,
  name: '',
  description: ''
});
const fileInput = ref(null);


// 初始化数据
onMounted(() => {
  loadCategories();
});

// 加载分类列表
const loadCategories = async () => {
  try {
    const response = await getCategoryList();
    if (response.data.code === 10000) {
      // 为每个分类初始化图片数量
      categories.value = response.data.data.categories.map(category => ({
        ...category,
        imageCount: category.images?.length || 0, // 使用后端返回的图片数量
        images: [] // 初始化时图片为空，需要单独加载
      }));
    }
  } catch (error) {
    console.error('加载分类列表失败:', error);
  }
};

// 显示添加表单
const showAddForm = () => {
  isEditing.value = false;
  form.value = { id: null, name: '', description: '' };
  showForm.value = true;
};
const router = useRouter();

// 编辑分类
const editCategory = async (category) => {
  // 跳转到分类编辑页面
  router.push(`/category/edit/${category.id}`);
};

// 保存分类
const saveCategory = async () => {
  if (!form.value.name.trim()) {
    ElMessage.warning('请输入分类名称');
    return;
  }

  try {
    if (isEditing.value) {
      // 更新现有分类 - 暂时只更新本地数据，实际项目中需要实现更新API
      const index = categories.value.findIndex(cat => cat.id === form.value.id);
      if (index !== -1) {
        categories.value[index] = { ...form.value };
      }
    } else {
      // 创建新分类
      const response = await createCategory({
        name: form.value.name,
        description: form.value.description
      });
      
      if (response.data.code === 10000) {
        // 添加新分类到列表
        ElMessage.success('分类创建成功');

        categories.value.push({
          ...response.data.data,
          images: []
        });
        
        // 重置表单
        cancelForm();
      } else {
        ElMessage.error(response.data.message || '创建分类失败');
      }
    }
  } catch (error) {
    console.error('保存分类失败:', error);
    ElMessage.error(error.message || '保存分类失败');
  }
};

// 取消表单
const cancelForm = () => {
  showForm.value = false;
  form.value = { id: null, name: '', description: '' };
};

// 删除分类
const deleteCategory = async (id) => {
  if (confirm('确定要删除这个分类吗？')) {
    try {
      const response = await apiDeleteCategory(id);
      if (response.data.code === 10000) {
        categories.value = categories.value.filter(cat => cat.id !== id);
        if (selectedCategory.value && selectedCategory.value.id === id) {
          selectedCategory.value = null;
        }
        ElMessage.success('分类删除成功');
      } else {
        ElMessage.error(response.data.message || '删除分类失败');
      }
    } catch (error) {
      console.log(error);
      
      console.error('删除分类失败:', error);
      ElMessage.error('删除分类失败: ' + (error.response?.data?.message || error.message));
    }
  }
};

// 选择分类
const selectCategory = async (category) => {
  selectedCategory.value = category;
  
  // 加载该分类下的图片
  try {
    const response = await getCategoryImages(category.id);
    if (response.data.code === 10000) {
      // 更新分类的图片列表
      const categoryIndex = categories.value.findIndex(cat => cat.id === category.id);
      if (categoryIndex !== -1) {
        categories.value[categoryIndex].images = response.data.data.images;
        // 更新图片数量
        categories.value[categoryIndex].imageCount = response.data.data.images.length;
      }
      selectedCategory.value.images = response.data.data.images;
      // 同时更新选中分类的图片数量
      selectedCategory.value.imageCount = response.data.data.images.length;
    }
  } catch (error) {
    console.error('加载分类图片失败:', error);
    selectedCategory.value.images = [];
  }
};

// 触发文件选择
const triggerFileSelect = () => {
  fileInput.value.click();
};

// 处理文件上传
const handleFileUpload = async (event) => {
  const files = Array.from(event.target.files);
  
  if (!selectedCategory.value) {
    ElMessage.warning('请先选择一个分类');
    return;
  }

  for (const file of files) {
    // 验证文件类型
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      ElMessage.error('只支持PNG、JPG、JPEG格式的图片');
      continue; // 继续处理下一个文件
    }

    // 验证文件大小 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      ElMessage.error('图片大小不能超过10MB');
      continue; // 继续处理下一个文件
    }

    if (file.type.startsWith('image/')) {
      try {
        // 创建FormData对象并添加文件
        const formData = new FormData();
        formData.append('image', file);
        
        // 调用现场还原上传图片接口
        const uploadResponse = await uploadImage(formData);
        
        if (uploadResponse.data.code === 10000) {
          // 将上传后的图片添加到当前分类（仅用于预览）
          if (!selectedCategory.value.images) {
            selectedCategory.value.images = [];
          }
          
          // 添加服务器返回的图片信息（用于保存时使用）
          selectedCategory.value.images.push({
            ...uploadResponse.data.data,
            file: file, // 保存原始文件引用，以便在保存时使用
            isLocal: true // 标记为本地新增的图片
          });
          
          // 同时更新分类列表中的图片
          const categoryIndex = categories.value.findIndex(cat => cat.id === selectedCategory.value.id);
          if (categoryIndex !== -1) {
            if (!categories.value[categoryIndex].images) {
              categories.value[categoryIndex].images = [];
            }
            categories.value[categoryIndex].images.push({
              ...uploadResponse.data.data,
              file: file,
              isLocal: true
            });
            
            // 更新图片数量
            categories.value[categoryIndex].imageCount = categories.value[categoryIndex].images.length;
          }
        } else {
          console.error('上传图片失败:', uploadResponse.data.message);
          ElMessage.error('上传图片失败: ' + uploadResponse.data.message);
        }
      } catch (error) {
        console.error('上传图片失败:', error);
        ElMessage.error('上传图片失败: ' + error.message);
      }
    }
  }

  // 清空文件输入框
  event.target.value = '';
};

// 处理图片URL
const processImageUrl = (filepath) => {
  if (!filepath) return '';
  // 如果是对象，获取image_url属性（后端返回的数据结构）
  if (typeof filepath === 'object' && filepath.image_url) {
    return filepath.image_url;
  }
  // 如果是对象，获取url属性（本地预览的数据结构）
  if (typeof filepath === 'object' && filepath.url) {
    return filepath.url;
  }
  // 如果是完整URL，直接返回
  if (typeof filepath === 'string' && filepath.startsWith('http')) {
    return filepath;
  }
  // 如果是相对路径，添加代理前缀
  if (typeof filepath === 'string' && filepath.startsWith('/')) {
    return filepath;
  }
  // 其他情况，添加/uploads前缀
  return '/uploads/' + filepath;
};

// 移除图片
const removeImage = async (index) => {
  if (selectedCategory.value && selectedCategory.value.images) {
    const image = selectedCategory.value.images[index];
    
    // 如果是已经保存的图片，需要调用API删除
    if (image.id && !image.isLocal) {
      if (confirm('确定要删除这张图片吗？')) {
        try {
          const response = await deleteCategoryImage(image.id);
          if (response.data.code === 10000) {
            selectedCategory.value.images.splice(index, 1);
            
            // 同时更新分类列表中的图片
            const categoryIndex = categories.value.findIndex(cat => cat.id === selectedCategory.value.id);
            if (categoryIndex !== -1) {
              categories.value[categoryIndex].images.splice(index, 1);
              
              // 更新图片数量
              categories.value[categoryIndex].imageCount = categories.value[categoryIndex].images.length;
            }
            
            ElMessage.success('图片删除成功');
          } else {
            ElMessage.error(response.data.message || '删除图片失败');
          }
        } catch (error) {
          console.error('删除图片失败:', error);
          ElMessage.error('删除图片失败: ' + error.message);
        }
      }
    } else {
      // 释放URL对象以避免内存泄漏（仅对本地预览图片）
      if (image && image.url && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
      selectedCategory.value.images.splice(index, 1);
      
      // 同时更新分类列表中的图片
      const categoryIndex = categories.value.findIndex(cat => cat.id === selectedCategory.value.id);
      if (categoryIndex !== -1) {
        categories.value[categoryIndex].images.splice(index, 1);
      }
    }
  }
};
</script>

<style scoped>
.category-management {
  padding: 20px;
  color: white;
  background-color: #000;
}

.header {
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  color: #5EFF00;
}

.category-list {
  margin-bottom: 20px;
  height: 80vh;
  overflow-y: scroll;
}
/* 垂直滚动条 */
::-webkit-scrollbar {
    width: 12px;
    /* 垂直滚动条宽度 */
    height: 12px;
    /* 水平滚动条高度 */
}

/* 滚动条轨道 */
::-webkit-scrollbar-track {
    background: #090909;
    border-radius: 6px;
}

/* 滚动条滑块 */
::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #121212, #939292);
    border-radius: 6px;
}

/* 滑块悬停效果 */
::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #666, #444);
}

/* 水平滚动条滑块 */
::-webkit-scrollbar-thumb:horizontal {
    background: linear-gradient(90deg, #000000, #000000);
}

/* 角落 */
::-webkit-scrollbar-corner {
    background: #000000;
}
.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  background: #2B2C2C;
  border-radius: 8px;
  border: 1px solid #2B2C2C;
}

.category-info {
  flex: 1;
  cursor: pointer;
}

.category-name {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 5px;
  color: #FFFFFF;
}

.category-description {
  font-size: 14px;
  color: #AAAAAA;
  margin-bottom: 5px;
}

.image-count {
  font-size: 12px;
  color: #5EFF00;
}

.category-actions {
  display: flex;
  gap: 10px;
}

.edit-btn, .delete-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.edit-btn {
  background: #5EFF00;
  color: #000;
}

.delete-btn {
  background: #FF4D4D;
  color: white;
}

.category-form {
  background: #2B2C2C;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.category-form h3 {
  margin-top: 0;
  color: #5EFF00;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #FFFFFF;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  background: #1A1A1A;
  border: 1px solid #2B2C2C;
  border-radius: 4px;
  color: #FFFFFF;
  box-sizing: border-box;
}

.form-group textarea {
  height: 80px;
  resize: vertical;
}

.form-actions {
  display: flex;
  gap: 10px;
}

.save-btn, .cancel-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.save-btn {
  background: #5EFF00;
  color: #000;
}

.cancel-btn {
  background: #666;
  color: white;
}

.add-category-btn {
  padding: 10px 20px;
  background: #5EFF00;
  color: #000;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 20px;
}

.image-management {
  margin-top: 30px;
}

.image-management h3 {
  color: #5EFF00;
  margin-bottom: 20px;
}

.upload-section {
  margin-bottom: 20px;
}

.upload-area {
  border: 2px dashed #5EFF00;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s;
}

.upload-area:hover {
  background-color: #2B2C2C;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 10px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 10px;
  box-sizing: border-box;
}

.image-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.image-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.image-actions {
  position: absolute;
  top: 5px;
  right: 5px;
}

.remove-btn {
  padding: 4px 8px;
  background: rgba(255, 77, 77, 0.8);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

/* Element Plus 上传组件样式 */
.image-uploader .el-upload {
  width: 100%;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  outline: 0;
}

.image-uploader .el-upload:hover {
  border-color: #5EFF00;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #2B2C2C;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 10px;
  border-bottom: 1px solid #333;
}

.modal-header h3 {
  margin: 0;
  color: #5EFF00;
}

.close-btn {
  background: none;
  border: none;
  color: #AAAAAA;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #FFFFFF;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #333;
}

.modal-footer button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.save-btn {
  background: #5EFF00;
  color: #000;
}

.cancel-btn {
  background: #666;
  color: white;
}
</style>